import {
	Download01,
	Eye,
	FileDownload02,
	MessageChatCircle,
	NotificationText,
	PieChart02,
	SearchRefraction,
	Settings01,
} from "@untitledui/icons";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router";

import type {
	NavItemDividerType,
	NavItemType,
} from "@/features/navigation/config";

import { Badge } from "@/components/ui/badges/badges";
import { ROUTES } from "@/constants/routes";
import { useDashboardEventsDispatch } from "@/context/event-filters-registry";
import { resetEventsFiltersToDefaults } from "@/context/events";
import { useWebSocket } from "@/context/websocket";
import { EVENT_FILTER_SCOPE } from "@/features/events/types/event-filter-scope";
import { SidebarNavigationSectionDividers } from "@/features/navigation/sidebar-navigation/sidebar-section-dividers";
import { useNavigationFeatureFlags } from "@/hooks/useNavigationFeatureFlags";
import { useLocationZoneSensorIdSets } from "@/hooks/useOrgLocationTree";
import { PERMISSIONS, usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/providers/auth-provider";

export const SidebarNavigation = () => {
	const location = useLocation();

	const { enableAnalytics, enableCaseManagement, enableSopManagement } =
		useNavigationFeatureFlags();

	const { checkPermission } = usePermissions();
	const canAccessWatches = checkPermission(PERMISSIONS.AccessWatches).allowed;
	const canExportEvents = checkPermission(PERMISSIONS.ExportData).allowed;
	const canViewActivityLogs = checkPermission(
		PERMISSIONS.ViewActivityLogs,
	).allowed;
	const canAccessLocations = checkPermission(
		PERMISSIONS.AccessLocations,
	).allowed;
	const canAccessUsers = checkPermission(PERMISSIONS.AccessUsers).allowed;

	const { orgCode } = useAuth();
	const { clearNewEventsCount, newEventsCount } = useWebSocket();
	const dashboardDispatch = useDashboardEventsDispatch();
	const idSets = useLocationZoneSensorIdSets();

	const handleBadgeClick = useCallback(() => {
		// Badge is dashboard-centric (WebSocket counter); always reset dashboard filters
		// even when the user clicks it from another route (e.g. All Activity). because clicking
		// the badge should reset the filters to the dashboard defaults while also taking
		// the user back to the dashboard and making the new events visible.
		resetEventsFiltersToDefaults(
			dashboardDispatch,
			idSets,
			orgCode ? { orgCode, scope: EVENT_FILTER_SCOPE.DASHBOARD } : undefined,
		);
		clearNewEventsCount();
	}, [clearNewEventsCount, dashboardDispatch, idSets, orgCode]);

	const navItemsWithDividers: (NavItemDividerType | NavItemType)[] = useMemo(
		() => [
			{
				badge:
					newEventsCount > 0 ? (
						<Badge onClick={handleBadgeClick} size="sm" type="modern">
							{newEventsCount}
						</Badge>
					) : undefined,
				href: ROUTES.DASHBOARD,
				icon: NotificationText,
				label: "Alerts Dashboard",
				testId: "nav-item-dashboard",
			},
			{
				hidden: !canViewActivityLogs,
				href: ROUTES.ALL_ACTIVITY,
				icon: SearchRefraction,
				label: "All Activity",
				testId: "nav-item-all-activity",
			},
			{
				hidden: !enableCaseManagement,
				href: ROUTES.CASE_MANAGEMENT,
				icon: FileDownload02,
				label: "Case Management",
				testId: "nav-item-case-management",
			},
			{
				hidden: !canExportEvents,
				href: ROUTES.EXPORTS,
				icon: Download01,
				label: "Exports",
				testId: "nav-item-exports",
			},
			{
				hidden: !enableAnalytics,
				href: ROUTES.ANALYTICS,
				icon: PieChart02,
				label: "Analytics",
				testId: "nav-item-analytics",
			},
			{
				hidden: !canAccessWatches,
				href: ROUTES.WATCHLIST,
				icon: Eye,
				label: "Watchlist",
				testId: "nav-item-watchlist",
			},
			{ divider: true },
			{
				// Spec §3.6 note: hide the whole Administration accordion when
				// the user holds neither `access_locations` nor `access_users`.
				hidden: !canAccessLocations && !canAccessUsers && !enableSopManagement,
				href: ROUTES.SETTINGS,
				icon: Settings01,
				items: [
					...(canAccessLocations
						? [
								{
									href: ROUTES.LOCATION_MANAGEMENT,
									label: "Location Management",
								},
							]
						: []),
					...(canAccessUsers
						? [{ href: ROUTES.USER_MANAGEMENT, label: "User Management" }]
						: []),
					...(enableSopManagement
						? [
								{
									href: ROUTES.SOP_MANAGEMENT,
									label: "SOP Management",
								},
							]
						: []),
				],
				label: "Administration",
				testId: "nav-item-settings",
			},
			{
				href: ROUTES.SUPPORT,
				icon: MessageChatCircle,
				label: "Support",
				testId: "nav-item-support",
			},
		],
		[
			canAccessLocations,
			canAccessUsers,
			canAccessWatches,
			canExportEvents,
			canViewActivityLogs,
			enableAnalytics,
			enableCaseManagement,
			enableSopManagement,
			newEventsCount,
			handleBadgeClick,
		],
	);
	return (
		<nav aria-label="Main navigation" data-testid="sidebar-navigation">
			<SidebarNavigationSectionDividers
				activeUrl={location.pathname}
				items={navItemsWithDividers}
			/>
		</nav>
	);
};
